import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getWidget, updateWidget } from "@/lib/api";
import { Copy, ExternalLink, Loader2, ShieldCheck, ShieldAlert } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const DISPLAY_MODES = [
  { v: "numeric", label: "Exact numerical score" },
  { v: "score_category", label: "Score + designation" },
  { v: "designation", label: "Verified Trust Profile (no number)" },
];

const LAYOUTS = [
  { v: "card", label: "Card (expanded)" },
  { v: "badge", label: "Badge (compact)" },
];

const THEMES = [
  { v: "dark", label: "Dark" },
  { v: "light", label: "Light" },
];

export default function WidgetPanel({ assessmentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const d = await getWidget(assessmentId);
      setData(d);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (!assessmentId) return;
    (async () => {
      setLoading(true);
      await refresh();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);

  const patch = async (updates) => {
    try {
      const d = await updateWidget(assessmentId, updates);
      setData(d);
    } catch (e) {
      toast.error("Could not save widget settings");
    }
  };

  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(data.embed_snippet);
      toast.success("Snippet copied to clipboard");
    } catch {
      toast.error("Copy failed — select the snippet manually.");
    }
  };

  if (loading) {
    return (
      <div className="pp-surface p-6 pp-label" data-testid="widget-loading">
        Loading widget…
      </div>
    );
  }
  if (!data) return null;

  const elig = data.eligibility || {};
  const eligible = elig.eligible_for_numeric_score;

  return (
    <div className="pp-surface" data-testid="widget-panel">
      <div className="px-6 py-5 border-b border-[#333C47] flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="pp-label mb-1 flex items-center gap-2">
            {eligible ? (
              <ShieldCheck className="h-3.5 w-3.5 text-[#6CC24A]" />
            ) : (
              <ShieldAlert className="h-3.5 w-3.5 text-[#EAB308]" />
            )}
            Public Trust Score Widget
          </div>
          <div className="font-display text-xl text-white">
            Turn your Trust Score into an embedded credibility badge.
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => patch({ widget_enabled: !data.widget_enabled })}
            data-testid="widget-toggle-enabled"
            className={`w-10 h-5 border ${
              data.widget_enabled
                ? "bg-[#6CC24A] border-[#6CC24A]"
                : "bg-[#0F1115] border-[#333C47]"
            } relative`}
            role="switch"
            aria-checked={data.widget_enabled}
          >
            <span
              className={`absolute top-[1px] w-[16px] h-[16px] transition-all ${
                data.widget_enabled ? "left-[22px] bg-[#0F1115]" : "left-[1px] bg-white"
              }`}
            />
          </button>
          <span className="text-xs text-[#C7CBD1]">
            {data.widget_enabled ? "Public embed on" : "Off (private)"}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 p-6">
        {/* Left: settings */}
        <div className="space-y-5">
          <div className="pp-card p-4" data-testid="widget-eligibility">
            <div className="pp-label mb-2">Eligibility</div>
            <div
              className={`text-sm ${
                eligible ? "text-[#6CC24A]" : "text-[#EAB308]"
              } font-medium leading-relaxed`}
            >
              {eligible
                ? "Meets every threshold for a public numeric Trust Score."
                : "Preliminary — numeric score is hidden on the public page."}
            </div>
            <ul className="mt-2 text-xs text-[#C7CBD1] space-y-1">
              {(elig.reasons || []).map((r, i) => (
                <li key={i}>· {r}</li>
              ))}
            </ul>
            {elig.expires_at && (
              <div className="mt-3 text-[11px] text-[#C7CBD1]">
                Valid until {new Date(elig.expires_at).toLocaleDateString()} —{" "}
                {elig.days_left ?? 0} day{elig.days_left === 1 ? "" : "s"} left.
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="pp-label block mb-2">Display mode</label>
              <Select
                value={data.widget_display_mode}
                onValueChange={(v) => patch({ widget_display_mode: v })}
              >
                <SelectTrigger
                  data-testid="widget-display-mode"
                  className="bg-[#0F1115] border border-[#333C47] rounded-none h-auto py-2.5 text-white focus:ring-0"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1F24] border border-[#333C47] rounded-none">
                  {DISPLAY_MODES.map((m) => (
                    <SelectItem
                      key={m.v}
                      value={m.v}
                      className="rounded-none focus:bg-[#232931] focus:text-white text-white"
                    >
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="pp-label block mb-2">Layout</label>
              <Select
                value={data.widget_layout}
                onValueChange={(v) => patch({ widget_layout: v })}
              >
                <SelectTrigger
                  data-testid="widget-layout"
                  className="bg-[#0F1115] border border-[#333C47] rounded-none h-auto py-2.5 text-white focus:ring-0"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1F24] border border-[#333C47] rounded-none">
                  {LAYOUTS.map((m) => (
                    <SelectItem
                      key={m.v}
                      value={m.v}
                      className="rounded-none focus:bg-[#232931] focus:text-white text-white"
                    >
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="pp-label block mb-2">Theme</label>
              <Select
                value={data.widget_theme}
                onValueChange={(v) => patch({ widget_theme: v })}
              >
                <SelectTrigger
                  data-testid="widget-theme"
                  className="bg-[#0F1115] border border-[#333C47] rounded-none h-auto py-2.5 text-white focus:ring-0"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1F24] border border-[#333C47] rounded-none">
                  {THEMES.map((m) => (
                    <SelectItem
                      key={m.v}
                      value={m.v}
                      className="rounded-none focus:bg-[#232931] focus:text-white text-white"
                    >
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="pp-label block mb-2">Embed snippet</label>
            <textarea
              readOnly
              value={data.embed_snippet}
              data-testid="widget-embed-snippet"
              className="w-full font-mono text-[11px] bg-[#0F1115] border border-[#333C47] px-3 py-2 text-[#C7CBD1] focus:outline-none min-h-[92px]"
            />
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={copySnippet}
                data-testid="widget-copy-snippet"
                className="pp-btn-primary text-xs flex items-center gap-2"
              >
                <Copy className="h-3.5 w-3.5" /> Copy embed code
              </button>
              <a
                href={data.verify_url}
                target="_blank"
                rel="noreferrer"
                data-testid="widget-open-verify"
                className="pp-btn-secondary text-xs flex items-center gap-2"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Open verification page
              </a>
            </div>
          </div>
        </div>

        {/* Right: live preview */}
        <div className="pp-card p-4" data-testid="widget-live-preview">
          <div className="pp-label mb-3">Live preview</div>
          <div className="bg-[#0F1115] border border-[#333C47] p-4 flex items-center justify-center min-h-[220px]">
            {data.widget_enabled ? (
              <img
                src={data.svg_url}
                alt="Trust Score badge"
                style={{ maxWidth: "100%", height: "auto" }}
                data-testid="widget-live-svg"
              />
            ) : (
              <div className="text-xs text-[#C7CBD1] text-center max-w-[220px]">
                Enable the widget to preview and copy the embed code.
              </div>
            )}
          </div>
          <div className="mt-3 text-[11px] text-[#C7CBD1] leading-relaxed">
            The badge updates automatically when your score changes. Deleted, expired,
            or preliminary assessments never display a numeric score.
          </div>
        </div>
      </div>
    </div>
  );
}
